import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import useMediaQuery from '@mui/material/useMediaQuery';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';

import Drawer from './Drawer';
import Header from './Header';
import HorizontalBar from './Drawer/HorizontalBar';
import Loader from 'components/Loader';
import Breadcrumbs from 'components/@extended/Breadcrumbs';
import AuthGuard from 'utils/route-guard/AuthGuard';

import { MenuOrientation, DRAWER_COLLAPSE_BREAKPOINT } from 'config';
import useConfig from 'hooks/useConfig';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { NavigationProvider } from "@/contexts/NavigationContext";
import { EDITOR_PAGE_NAME } from "@/constants/globalConstants";
import RightDrawer from "@/pages/custom-components/CommonComponents/RightDrawer";
import { RIGHT_DRAWER_WIDTH } from "@/constants/styles";

// IMPORTANTE: Importiamo lo stato del drawer destro!
import { useRightDrawer } from "@/contexts/application/RightDrawerContext";

export default function DashboardLayout() {
    const { pathname } = useLocation();
    const { menuMaster, menuMasterLoading } = useGetMenuMaster();
    const { isOpen: isRightDrawerOpen } = useRightDrawer(); // <--- Leggiamo se il destro è aperto

    const downXL = useMediaQuery((theme) => theme.breakpoints.down('xl'));
    const downLG = useMediaQuery((theme) => theme.breakpoints.down(DRAWER_COLLAPSE_BREAKPOINT));

    const { container, miniDrawer, menuOrientation } = useConfig();
    const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

    //Serve a scrollare sempre a 0 le pagine quando cambia l'url
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

        const mainContainer = document.querySelector('main');
        if (mainContainer) {
            mainContainer.scrollTop = 0;
        }
    }, [pathname]); // Si attiva ogni volta che cambi rotta

    useEffect(() => {
        if (!miniDrawer) {
            handlerDrawerOpen(!downXL);
        }
    }, [downXL]);

    if (menuMasterLoading) return <Loader />;

    const isDrawerOpen = menuMaster?.isDashboardDrawerOpened;

    // Calcoliamo la larghezza dinamicamente per stringere il main
    const leftWidth = isDrawerOpen ? 260 : 60;
    const rightWidth = isRightDrawerOpen ? parseInt(RIGHT_DRAWER_WIDTH.replace("px", "")) : 0;
    const totalDrawersWidth = leftWidth + rightWidth;

    return (
        <AuthGuard>
            <NavigationProvider>
                <Box
                    sx={{
                        display: 'flex',
                        width: '100%',
                        minHeight: '100.1vh',
                        overflowY: 'visible'
                    }}
                >
                    <Header />
                    {!isHorizontal ? <Drawer /> : <HorizontalBar />}

                    <Box
                        component="main"
                        sx={{
                            width: downLG ? '100%' : `calc(100% - ${totalDrawersWidth}px)`,
                            flexGrow: 1,
                            p: pathname.includes('/editor') ? { xs: 0 } : { xs: 2, sm: 2 },
                            transition: (theme) => theme.transitions.create(['width', 'margin'], {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.enteringScreen,
                            }),
                        }}
                    >
                        <Toolbar sx={{ mt: isHorizontal ? 8 : 'inherit' }} />
                        <Container
                            maxWidth={false}
                            sx={{
                                ...(container && { px: { xs: 0, sm: 0 } }),
                                position: 'relative',
                                minHeight: 'calc(100vh - 110px)',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            {pathname !== '#!' && !pathname.includes(EDITOR_PAGE_NAME) && <Breadcrumbs />}                            
                            <Outlet />
                        </Container>
                    </Box>

                    {/* Il Right Drawer va fuori dal container, affiancato al main */}
                    <RightDrawer />
                </Box>
            </NavigationProvider>
        </AuthGuard>
    );
}